import { t } from '../i18n';
import { grabFrameJpegRetrying, translateSign, TranslateFail, type TranslateResult } from '../services/ocrTranslateService';
import { speak } from '../services/voiceService';

/**
 * 🔤 Snapshot sign-translation flow (spec: OCR_TRANSLATE_SPEC.md §5).
 * Framing state keeps the camera view fully visible (reticle + bottom bar
 * only); ✓ grabs one frame → "Reading…" → bottom sheet with the original
 * text, the translation in the visitor's language, 🔊 speak and 📋 copy.
 *
 * Cap behavior (owner spec): the venue's daily cap surfaces to visitors as
 * a neutral "unavailable — ask venue staff" message with no cost details;
 * after 2 cap responses the flow stops calling the server for the rest of
 * the session (opens go straight to the message).
 */
export class TranslateOverlay {
  private el: HTMLDivElement | null = null;
  private capHits = 0;

  constructor(private getCanvas: () => HTMLCanvasElement | null) {}

  get isOpen(): boolean {
    return this.el !== null;
  }

  open(): void {
    if (this.el) return;
    const wrap = document.createElement('div');
    wrap.className = 'tr-overlay';
    this.el = wrap;
    document.body.appendChild(wrap);
    if (this.capHits >= 2) {
      this.renderMessage(t('tr.capped'), false);
      return;
    }
    this.renderFraming();
  }

  close(): void {
    this.el?.remove();
    this.el = null;
  }

  // ---------- states ----------

  private renderFraming(): void {
    const wrap = this.el;
    if (!wrap) return;
    wrap.innerHTML = `
      <div class="tr-reticle"></div>
      <div class="tr-bar">
        <p class="tr-hint"></p>
        <div class="tr-btns">
          <button class="btn primary tr-go">✓</button>
          <button class="btn ghost tr-cancel">✕</button>
        </div>
      </div>`;
    (wrap.querySelector('.tr-hint') as HTMLElement).textContent = t('tr.frame');
    (wrap.querySelector('.tr-cancel') as HTMLButtonElement).addEventListener('click', () => this.close());
    (wrap.querySelector('.tr-go') as HTMLButtonElement).addEventListener('click', () => void this.capture());
  }

  private async capture(): Promise<void> {
    const wrap = this.el;
    const canvas = this.getCanvas();
    if (!wrap) return;
    if (!canvas) {
      this.renderMessage(t('tr.error'), true);
      return;
    }
    wrap.innerHTML = `<div class="tr-bar"><p class="tr-hint tr-reading"></p></div>`;
    (wrap.querySelector('.tr-hint') as HTMLElement).textContent = t('tr.reading');
    try {
      // Retries client-side if the grabbed frame reads as blank (WebGL
      // canvas read racing the render loop — see ocrTranslateService.ts
      // doc comment) BEFORE spending a paid vision call on known garbage.
      const image = await grabFrameJpegRetrying(canvas);
      const res = await translateSign(image);
      this.renderResult(res);
    } catch (err) {
      if (err instanceof TranslateFail && err.kind === 'cap') {
        this.capHits++;
        this.renderMessage(t('tr.capped'), false);
      } else if (err instanceof TranslateFail && err.kind === 'blank') {
        this.renderMessage(t('tr.blank'), true);
      } else if (err instanceof TranslateFail && err.kind === 'unreadable') {
        this.renderMessage(t('tr.unreadable'), true);
      } else {
        this.renderMessage(t('tr.error'), true);
      }
    }
  }

  private renderMessage(text: string, retry: boolean): void {
    const wrap = this.el;
    if (!wrap) return;
    wrap.innerHTML = `
      <div class="tr-bar">
        <p class="tr-hint"></p>
        <div class="tr-btns">
          ${retry ? '<button class="btn primary tr-retry">↻</button>' : ''}
          <button class="btn ghost tr-cancel">✕</button>
        </div>
      </div>`;
    (wrap.querySelector('.tr-hint') as HTMLElement).textContent = text;
    (wrap.querySelector('.tr-cancel') as HTMLButtonElement).addEventListener('click', () => this.close());
    wrap.querySelector('.tr-retry')?.addEventListener('click', () => this.renderFraming());
  }

  private renderResult(res: TranslateResult): void {
    const wrap = this.el;
    if (!wrap) return;
    wrap.innerHTML = `
      <div class="tr-sheet">
        <p class="tr-translated"></p>
        <details class="tr-orig">
          <summary></summary>
          <p class="tr-orig-text"></p>
        </details>
        <div class="tr-btns">
          <button class="btn ghost tr-speak"></button>
          <button class="btn ghost tr-copy"></button>
          <button class="btn primary tr-cancel">✕</button>
        </div>
      </div>`;
    // All strings via textContent — recognized sign text is untrusted.
    (wrap.querySelector('.tr-translated') as HTMLElement).textContent = res.translated;
    (wrap.querySelector('.tr-orig > summary') as HTMLElement).textContent = t('tr.original', {
      lang: res.sourceLang,
    });
    (wrap.querySelector('.tr-orig-text') as HTMLElement).textContent = res.original;
    const speakBtn = wrap.querySelector('.tr-speak') as HTMLButtonElement;
    speakBtn.textContent = t('tr.speak');
    speakBtn.addEventListener('click', () => speak(res.translated, `tr-${Date.now()}`, 0));
    const copyBtn = wrap.querySelector('.tr-copy') as HTMLButtonElement;
    copyBtn.textContent = t('tr.copy');
    copyBtn.addEventListener('click', () => {
      void navigator.clipboard?.writeText(`${res.original}\n${res.translated}`);
      copyBtn.textContent = t('tr.copied');
      window.setTimeout(() => (copyBtn.textContent = t('tr.copy')), 1500);
    });
    (wrap.querySelector('.tr-cancel') as HTMLButtonElement).addEventListener('click', () => this.close());
  }
}
